import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getDepartment, createDepartment, updateDepartment, DepartmentCreate, DepartmentUpdate } from '../../api/data';

const DepartmentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    head_name: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    if (isEdit && id) {
      loadDepartment(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadDepartment = async (deptId: number) => {
    try {
      const dept = await getDepartment(deptId);
      setFormData({
        name_ar: dept.name_ar,
        name_en: dept.name_en,
        head_name: dept.head_name || '',
        contact_email: dept.contact_email || '',
        contact_phone: dept.contact_phone || ''
      });
    } catch (error) {
      alert('Failed to load department');
      navigate('/data/departments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit && id) {
        const data: DepartmentUpdate = { ...formData };
        Object.keys(data).forEach(key => {
          if (data[key as keyof DepartmentUpdate] === '') {
            data[key as keyof DepartmentUpdate] = undefined;
          }
        });
        await updateDepartment(parseInt(id), data);
        alert('Department updated successfully');
      } else {
        const data: DepartmentCreate = { ...formData };
        await createDepartment(data);
        alert('Department created successfully');
      }
      
      navigate('/data/departments');
    } catch (error: any) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        <div className="card p-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-primary-500">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold text-primary-900 tracking-tight leading-tight">
          {isEdit ? 'Edit Department' : 'Create Department'}
        </h1>
        <Link to="/data/departments" className="btn-ghost text-xs gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        <div>
          <label className="label">Arabic Name <span className="text-error">*</span></label>
          <input
            type="text"
            value={formData.name_ar}
            onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
            required
            className="input-base"
            placeholder="اسم القسم بالعربية"
          />
        </div>

        <div>
          <label className="label">English Name <span className="text-error">*</span></label>
          <input
            type="text"
            value={formData.name_en}
            onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
            required
            className="input-base"
            placeholder="Department Name in English"
          />
        </div>

        <div>
          <label className="label">Department Head</label>
          <input
            type="text"
            value={formData.head_name}
            onChange={(e) => setFormData({ ...formData, head_name: e.target.value })}
            className="input-base"
            placeholder="Head of Department Name"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Contact Email</label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="input-base"
              placeholder="dept@najah.edu"
            />
          </div>
          <div>
            <label className="label">Contact Phone</label>
            <input
              type="text"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              className="input-base"
              placeholder="+970-9-1234567"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-3">
          <button type="submit" disabled={saving} className="btn-primary gap-1.5">
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Saving...
              </>
            ) : (isEdit ? 'Update Department' : 'Create Department')}
          </button>
          <Link to="/data/departments" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default DepartmentForm;
